/**
 * Who may see and do what, as far as the SPA needs to know (D-008, D-015).
 *
 * The API is the authority: row-level security decides what a person can read
 * and the write routes refuse a client viewer outright. This module exists so
 * the SPA agrees with it — a client viewer who is shown the monitoring page,
 * a "New case" button or the reports list is shown a door that opens onto a
 * 403 or an empty page, which reads as "broken", not as "not for you".
 *
 * Vue-free, like the rest of `utils/`, so the rules are unit-testable and the
 * router guard, the sidebar and the pages all read the same answers.
 */

/** The account tiers the API issues (`users.user_kind`). */
export type UserKind = 'firm_main' | 'firm_manager' | 'client_viewer' | 'superadmin' | ''

/**
 * A client viewer: read-only, and only the attached companies' brands that
 * have an open case — never a match, never a report (D-008).
 */
export function isViewer(kind: string | null | undefined): boolean {
  return kind === 'client_viewer'
}

/**
 * The firm's main account: the only persona that may change the firm's own
 * settings (name, national registry connections) or delete a case.
 */
export function isMain(kind: string | null | undefined): boolean {
  return kind === 'firm_main'
}

/**
 * May this person write at all? Anything but a client viewer.
 *
 * Deliberately "not a viewer" rather than "is staff": in mock mode the fixture
 * user carries no kind, and the mock workspace must stay fully usable.
 */
export function canWrite(kind: string | null | undefined): boolean {
  return !isViewer(kind)
}

/** Where a client viewer starts: their cases are the whole of their window. */
export const VIEWER_HOME = { name: 'oppositions' } as const

/**
 * Whether a route is open to this person. Routes declare `meta.staff: true`
 * when a client viewer has nothing there — the matcher's output (invisible to
 * viewers by policy), the reports (a 403 for viewers), every create form (a
 * write) and the dashboard (built from matches, so all zeros for a viewer).
 */
export function routeAllowed(
  meta: Record<string | number | symbol, unknown> | null | undefined,
  kind: string | null | undefined
): boolean {
  /* `meta.main: true` — the firm's main account only: the People screens,
   * which create accounts and decide what each person can see. The API
   * refuses everyone else (`AdminCtx`); a kind-less fixture user passes, as
   * `canWrite` explains. */
  if (meta?.main && kind && !isMain(kind)) return false
  if (!meta?.staff) return true
  return !isViewer(kind)
}

/** The sidebar entries a client viewer has no use for (see `routeAllowed`). */
const STAFF_NAV = new Set(['dashboard', 'monitoring', 'reports'])

export interface NavEntry {
  key: string
}

/** The sidebar tree as this person should see it. */
export function navFor<T extends NavEntry>(
  items: readonly T[],
  kind: string | null | undefined
): T[] {
  if (!isViewer(kind)) return [...items]
  return items.filter((item) => !STAFF_NAV.has(item.key))
}
